import { useEffect, useRef, useState } from 'react';
import { Dropdown, IconButton, Checkbox, tokens } from '@hotjar/hotsauce';
import { useTippy } from '@hotjar/webapp/hooks/useTippy';
import styled from '@emotion/styled';
import { hideOnEsc } from '@hotjar/global-filters/src/utils/hideOnEsc';
import type {
  ColumnOption,
  ColumnOptions,
} from '@hotjar/feedback/domain/Surveys/Responses/SurveyResponsesColumns';
import { useTranslation } from '@hotjar/translations';

const ColumnsContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  padding: ${tokens.spacer.x1} 0;
  flex-direction: column;
  grid-gap: ${tokens.spacer.x1};
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 0 ${tokens.spacer.x1};
  text-transform: none;
  margin: 0;
`;

export interface ColumnPickerProps<ColumnType extends string = string> {
  columns: ColumnOptions<ColumnType>;
  onColumnsUpdated: (columns: ColumnOptions<ColumnType>) => void;
}

export const ColumnsPicker = <ColumnType extends string>(
  props: ColumnPickerProps<ColumnType>,
): JSX.Element => {
  const { columns, onColumnsUpdated } = props;
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean | null>(null);
  const dropdownTriggerButton = useRef<HTMLButtonElement | null>(null);
  const { t } = useTranslation('feedback', { keyPrefix: 'surveys.responses-list' });

  const tippy = useTippy();

  function focusDropdownContent() {
    const dropdownContent = tippy.get()?.popper;
    const firtFocusableElement = dropdownContent?.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ) as HTMLInputElement | null;
    firtFocusableElement?.focus();
  }

  function onColumnVisibilityToggled(columnKey: ColumnType) {
    onColumnsUpdated({
      ...columns,
      [columnKey]: {
        ...columns[columnKey],
        show: !columns[columnKey].show,
      },
    });
  }

  useEffect(() => {
    if (isDropdownVisible === true) {
      focusDropdownContent();
    } else if (isDropdownVisible === false) {
      /* We don't want the outline to be visible on the first render */
      dropdownTriggerButton?.current?.focus();
    }
  });

  return (
    <Dropdown
      trigger={
        <IconButton
          ref={dropdownTriggerButton}
          size="s"
          kind="ghost"
          icon="table_cols"
          description={t('adjust-columns-visibility')}
          tooltipText={t('columns')}
        />
      }
      onCreate={(instance) => tippy.set(instance)}
      onHide={() => setIsDropdownVisible(false)}
      onShow={() => setIsDropdownVisible(true)}
      plugins={[hideOnEsc]}
    >
      <ColumnsContainer>
        {Object.entries<ColumnOption>(columns).map(([columnKey, column]) => {
          if (column.showInPanel === false) return null;
          return (
            <StyledCheckbox
              key={columnKey}
              labelContent={column.label}
              isDisabled={column.isDisabled}
              isChecked={column.show}
              onChange={() => onColumnVisibilityToggled(columnKey as ColumnType)}
            />
          );
        })}
      </ColumnsContainer>
    </Dropdown>
  );
};
